import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, MoreHorizontal, Eye, Pencil, Trash, Building2, Calendar as CalendarIcon, TrendingUp, ChevronLeft, ChevronRight, LayoutGrid, ChevronDown, ChevronUp, BarChart3, Filter, Check, X, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { KanbanGroups } from '../components/pipeline/KanbanGroups';
import { useUser } from '../context/UserContext';
import OpportunityDialog from '../components/OpportunityDialog';
import ExportMenu from '../components/ExportMenu';
import OpportunityAnalytics from '../components/OpportunityAnalyticsV2';
import OpportunityValoradas from '../components/OpportunityValoradas';
import {
  formatEuro,
  getOpportunityScope,
  displayTitle as displayOppTitle,
  getFinalAwardAmount,
  getFinalTdsAmount,
  getExtensionsSum,
} from '../utils/money';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const stages = ['Detectada', 'Propuesta', 'Presentada', 'Ganada', 'Perdida', 'Descartada'];

const stageColors = {
  'Detectada': 'border-l-blue-500',
  'Propuesta': 'border-l-yellow-500',
  'Presentada': 'border-l-purple-500',
  'Ganada': 'border-l-green-500',
  'Perdida': 'border-l-red-500',
  'Descartada': 'border-l-gray-500',
};

const stageBadgeColors = {
  'Detectada': 'bg-blue-100 text-blue-700',
  'Propuesta': 'bg-yellow-100 text-yellow-700',
  'Presentada': 'bg-purple-100 text-purple-700',
  'Ganada': 'bg-green-100 text-green-700',
  'Perdida': 'bg-red-100 text-red-700',
  'Descartada': 'bg-gray-100 text-gray-700',
};

const relevanceBadgeColors = {
  'Alta': 'bg-red-100 text-red-700',
  'Media': 'bg-yellow-100 text-yellow-700',
  'Baja': 'bg-green-100 text-green-700',
};

const DAYS_ES = ['LU', 'MA', 'MI', 'JU', 'VI', 'SA', 'DO'];
const MONTHS_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const StageCollapseButton = ({ stage, collapsed, onToggle, testIdPrefix = 'stage-collapse' }) => (
  <button
    type="button"
    onClick={() => onToggle(stage)}
    title={collapsed ? 'Desplegar estado' : 'Recoger estado'}
    aria-label={collapsed ? `Desplegar ${stage}` : `Recoger ${stage}`}
    data-testid={`${testIdPrefix}-${stage.toLowerCase()}`}
    className="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors flex-shrink-0"
  >
    {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
  </button>
);

const GroupBySelector = ({ value, onChange }) => (
  <div className="flex items-center gap-1 bg-muted/50 p-1 rounded-lg flex-shrink-0" data-testid="group-by-selector">
    <span className="text-xs text-muted-foreground px-1 hidden sm:inline">Agrupar por</span>
    {[['company', 'Empresa'], ['client_type', 'Tipo de cliente']].map(([k, label]) => (
      <button
        key={k}
        type="button"
        onClick={() => onChange(k)}
        data-testid={`group-by-${k}`}
        className={`px-2 py-1 rounded-md text-xs sm:text-sm font-medium transition-colors ${value === k ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
      >
        {label}
      </button>
    ))}
  </div>
);

const Pipeline = () => {
  const navigate = useNavigate();
  const { isEditor, currentUser } = useUser();
  const [opportunities, setOpportunities] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [typsaOrgUnits, setTypsaOrgUnits] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOpportunity, setEditingOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [viewMode, setViewMode] = useState('kanban');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [collapsedStages, setCollapsedStages] = useState({});
  const [groupBy, setGroupBy] = useState('company'); // 'company' | 'client_type'
  
  // Filtro por tipo de cliente (TYPSA, Privada, Pública)
  const [clientTypeFilter, setClientTypeFilter] = useState(['typsa_gg', 'typsa_colab', 'privada', 'publica']);
  
  // Filtro por periodo
  const [periodFilter, setPeriodFilter] = useState('all'); // 'all', 'week', 'month', 'quarter', 'year', 'custom'
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  
  // Filtro por responsable - por defecto "Yo" (usuario actual)
  const [ownerFilter, setOwnerFilter] = useState('me'); // 'all', 'me', or specific owner name
  
  // Mobile view state
  const [mobileActiveStage, setMobileActiveStage] = useState('Detectada');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [oppsRes, companiesRes, typsaUnitsRes] = await Promise.all([
        axios.get(`${API}/opportunities`),
        axios.get(`${API}/companies`),
        axios.get(`${API}/typsa-org-units`)
      ]);
      setOpportunities(oppsRes.data);
      setCompanies(companiesRes.data);
      setTypsaOrgUnits(typsaUnitsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCompanyName = (companyId) => {
    if (!companyId) return null;
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : null;
  };

  const getCompanyType = (companyId) => {
    if (!companyId) return null;
    const company = companies.find(c => c.id === companyId);
    return company ? company.company_type : null;
  };

  // Company lookup used by getOpportunityScope from utils/money.js
  const getCompanyLookup = () => {
    const map = {};
    companies.forEach((c) => { map[c.id] = c; });
    return map;
  };

  const getTypsaUnitInfo = (unitId) => {
    if (!unitId) return null;
    const unit = typsaOrgUnits.find(u => u.id === unitId);
    return unit ? { category: unit.category, value: unit.value } : null;
  };

  // Determine the client type of an opportunity — 4-way key aligned with
  // valoradas.py: 'typsa_gg' | 'typsa_colab' | 'privada' | 'publica'.
  const getOpportunityClientType = (opp) => {
    if (opp.typsa_org_unit_id) {
      return opp.is_typsa_collaboration ? 'typsa_colab' : 'typsa_gg';
    }
    if (opp.company_id) {
      const company = companies.find(c => c.id === opp.company_id);
      if (company) {
        if (company.company_type === 'typsa') {
          return opp.is_typsa_collaboration ? 'typsa_colab' : 'typsa_gg';
        }
        return company.company_type; // 'privada' or 'publica'
      }
    }
    return null;
  };

  // Toggle client type filter
  const toggleClientTypeFilter = (type) => {
    setClientTypeFilter(prev => {
      if (prev.includes(type)) {
        // Don't allow deselecting all filters
        if (prev.length === 1) return prev;
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  // Helper function to get period date range
  const getPeriodDateRange = (period) => {
    const now = new Date();
    let from, to;
    
    switch (period) {
      case 'week': {
        // Current week (Monday to Sunday)
        const dayOfWeek = now.getDay();
        const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Adjust for Monday start
        from = new Date(now);
        from.setDate(now.getDate() - diff);
        from.setHours(0, 0, 0, 0);
        to = new Date(from);
        to.setDate(from.getDate() + 6);
        to.setHours(23, 59, 59, 999);
        break;
      }
      case 'month': {
        // Current month
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        to = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        break;
      }
      case 'quarter': {
        // Current quarter
        const quarter = Math.floor(now.getMonth() / 3);
        from = new Date(now.getFullYear(), quarter * 3, 1);
        to = new Date(now.getFullYear(), quarter * 3 + 3, 0, 23, 59, 59, 999);
        break;
      }
      case 'year': {
        // Current year
        from = new Date(now.getFullYear(), 0, 1);
        to = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
        break;
      }
      case 'custom': {
        from = customDateRange.from;
        to = customDateRange.to;
        break;
      }
      default:
        return null;
    }
    
    return { from, to };
  };

  // Filter opportunities by client type, period, and owner
  const getFilteredOpportunities = () => {
    return opportunities.filter(opp => {
      // Filter by client type
      const clientType = getOpportunityClientType(opp);
      if (!clientType || !clientTypeFilter.includes(clientType)) {
        return false;
      }
      
      // Filter by owner
      if (ownerFilter === 'me') {
        if (opp.owner !== currentUser?.name) return false;
      } else if (ownerFilter === 'unassigned') {
        if (opp.owner && opp.owner.trim() !== '') return false;
      } else if (ownerFilter !== 'all') {
        if (opp.owner !== ownerFilter) return false;
      }
      
      // Filter by period — usa la fecha del último cambio de estado
      // (state_history), es decir la fecha en que la oportunidad entró en
      // su estado actual. Fallback: created_at.
      if (periodFilter !== 'all') {
        const dateRange = getPeriodDateRange(periodFilter);
        if (dateRange && dateRange.from && dateRange.to) {
          let refDate = null;
          const hist = Array.isArray(opp.state_history) ? opp.state_history : [];
          for (const e of hist) {
            const t = e && (e.timestamp || e.date);
            if (!t) continue;
            const d = new Date(t);
            if (isNaN(d)) continue;
            if (!refDate || d > refDate) refDate = d;
          }
          if (!refDate && opp.created_at) {
            const d = new Date(opp.created_at);
            if (!isNaN(d)) refDate = d;
          }
          if (!refDate) return false;
          if (refDate < dateRange.from || refDate > dateRange.to) {
            return false;
          }
        } else if (periodFilter === 'custom' && (!dateRange?.from || !dateRange?.to)) {
          // Custom range not fully set, show all
          return true;
        }
      }
      
      return true;
    });
  };

  // Get period label for display
  const getPeriodLabel = (period) => {
    switch (period) {
      case 'all': return 'Todo';
      case 'week': return 'Semana';
      case 'month': return 'Mes';
      case 'quarter': return 'Trimestre';
      case 'year': return 'Año';
      case 'custom': {
        if (customDateRange.from && customDateRange.to) {
          const formatDate = (d) => d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
          return `${formatDate(customDateRange.from)} - ${formatDate(customDateRange.to)}`;
        }
        return 'Personalizado';
      }
      default: return 'Periodo';
    }
  };

  // Clear period filter
  const clearPeriodFilter = () => {
    setPeriodFilter('all');
    setCustomDateRange({ from: null, to: null });
  };

  // Get unique owners from opportunities
  const getUniqueOwners = () => {
    const owners = opportunities
      .map(opp => opp.owner)
      .filter(owner => owner && owner.trim() !== '')
      .filter((owner, index, self) => self.indexOf(owner) === index)
      .sort((a, b) => a.localeCompare(b, 'es'));
    return owners;
  };

  const handleView = (opp) => {
    navigate(`/pipeline/${opp.id}`);
  };

  const handleEdit = (opp) => {
    setEditingOpportunity(opp);
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    setDeleting(deleteConfirm.id);
    try {
      await axios.delete(`${API}/opportunities/${deleteConfirm.id}`);
      toast.success('Oportunidad eliminada');
      fetchData();
    } catch (error) {
      console.error('Error deleting opportunity:', error);
      toast.error('Error al eliminar la oportunidad');
    } finally {
      setDeleting(null);
      setDeleteConfirm(null);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingOpportunity(null);
  };

  const toggleGroup = (groupKey, defaultExpanded = false) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !(prev[groupKey] ?? defaultExpanded)
    }));
  };

  const toggleStage = (stage) => {
    setCollapsedStages(prev => ({ ...prev, [stage]: !prev[stage] }));
  };

  const getOpportunitiesByStage = (stage) => {
    const filteredOpps = getFilteredOpportunities();
    return filteredOpps.filter((opp) => {
      const oppStage = opp.stage || 'Detectada';
      const legacyMapping = {
        'Lead': 'Detectada',
        'Qualified': 'Propuesta',
        'Proposal': 'Propuesta',
        'Negotiation': 'Presentada',
        'Won': 'Ganada',
        'Lost': 'Perdida',
      };
      const mappedStage = legacyMapping[oppStage] || oppStage;
      return mappedStage === stage;
    });
  };

  // Group opportunities by company within a stage
  // TYPSA units are flat groups (one per unit and kind), listed before companies
  const getGroupedOpportunitiesByStage = (stage) => {
    const stageOpps = getOpportunitiesByStage(stage);
    const groups = {};
    // Dos buckets TYPSA separados: GG (I+D+i) y Colaboración TYPSA.
    const typsaGroups = {
      typsa_gg:    { categories: {} },
      typsa_colab: { categories: {} },
    };
    
    stageOpps.forEach(opp => {
      // Check if it's a TYPSA opportunity
      if (opp.typsa_org_unit_id) {
        const bucketKey = opp.is_typsa_collaboration ? 'typsa_colab' : 'typsa_gg';
        const bucket = typsaGroups[bucketKey];
        const unitInfo = getTypsaUnitInfo(opp.typsa_org_unit_id);
        if (unitInfo) {
          const category = unitInfo.category || 'sin_categoria';
          const unitName = unitInfo.value || 'Sin unidad';
          if (!bucket.categories[category]) bucket.categories[category] = { units: {} };
          if (!bucket.categories[category].units[unitName]) bucket.categories[category].units[unitName] = [];
          bucket.categories[category].units[unitName].push(opp);
        } else {
          if (!bucket.categories['sin_categoria']) bucket.categories['sin_categoria'] = { units: {} };
          if (!bucket.categories['sin_categoria'].units['Sin unidad']) bucket.categories['sin_categoria'].units['Sin unidad'] = [];
          bucket.categories['sin_categoria'].units['Sin unidad'].push(opp);
        }
      } else {
        // Non-TYPSA opportunity - group by company
        const companyName = getCompanyName(opp.company_id) || 'Sin empresa';
        if (!groups[companyName]) {
          groups[companyName] = [];
        }
        groups[companyName].push(opp);
      }
    });
    
    const result = [];
    const categoryOrder = ['division', 'departamento', 'territorio', 'grupo', 'sin_categoria'];
    const pushTypsaUnits = (bucketKey) => {
      const bucket = typsaGroups[bucketKey];
      Object.keys(bucket.categories)
        .sort((a, b) => categoryOrder.indexOf(a) - categoryOrder.indexOf(b))
        .forEach(category => {
          Object.keys(bucket.categories[category].units)
            .sort((a, b) => a.localeCompare(b, 'es'))
            .forEach(unitName => {
              result.push({
                key: `${bucketKey}-${unitName}`,
                companyName: unitName,
                typsaKind: bucketKey,
                opportunities: bucket.categories[category].units[unitName]
              });
            });
        });
    };
    pushTypsaUnits('typsa_gg');
    pushTypsaUnits('typsa_colab');
    
    // Add other company groups (sorted alphabetically)
    const sortedKeys = Object.keys(groups).sort((a, b) => a.localeCompare(b, 'es'));
    sortedKeys.forEach(key => {
      // Get company type from the first opportunity in the group
      const firstOpp = groups[key][0];
      const companyType = firstOpp ? getCompanyType(firstOpp.company_id) : null;
      
      result.push({
        companyName: key,
        isTypsa: false,
        companyType: companyType, // 'privada' or 'publica'
        opportunities: groups[key]
      });
    });
    
    return result;
  };

  // ─── Feb 2026 spec (PR-A): subtotals only from offered_amount.
  // Discarded opps never contribute to portfolio aggregates.
  const getStageSubtotal = (stage) => {
    const opps = getOpportunitiesByStage(stage);
    if (stage === 'Descartada' || stage === 'Detectada' || stage === 'Propuesta') {
      // For stages where offered_amount is not yet defined, we skip aggregates.
      // Detectada / Propuesta rarely have offered_amount → we still expose them
      // if any offered_amount exists (edge case), otherwise report as "sin oferta".
    }
    let offeredSum = 0;
    let tdsSum = 0;
    let extensionsSum = 0;
    let withOffered = 0;
    let withoutOffered = 0;
    // Iter 95: en Ganada, la cartera contratada se ve reflejada como
    // importe adjudicado + ampliaciones (contract_extensions).
    const isGanada = stage === 'Ganada';
    opps.forEach((o) => {
      if (isGanada) {
        const finalAward = getFinalAwardAmount(o);
        const finalTds   = getFinalTdsAmount(o);
        const extSum     = getExtensionsSum(o);
        if (finalAward !== null && finalAward > 0) {
          offeredSum += finalAward;
          if (finalTds !== null && finalTds > 0) tdsSum += finalTds;
          extensionsSum += extSum;
          withOffered += 1;
        } else {
          withoutOffered += 1;
        }
        return;
      }
      const off = Number(o.offered_amount);
      if (o.offered_amount !== null && o.offered_amount !== undefined && !Number.isNaN(off) && off > 0) {
        offeredSum += off;
        const tds = Number(o.tds_amount);
        if (!Number.isNaN(tds) && tds > 0) tdsSum += tds;
        withOffered += 1;
      } else {
        withoutOffered += 1;
      }
    });
    // Discarded opps are excluded from portfolio aggregates by convention:
    // report as "N oportunidades · sin importe ofertado" (they only carry
    // estimated_value which is presupuesto base, not cartera).
    if (stage === 'Descartada') {
      return { offeredSum: 0, tdsSum: 0, extensionsSum: 0, withOffered: 0, withoutOffered: opps.length, isPortfolio: false };
    }
    return { offeredSum, tdsSum, extensionsSum, withOffered, withoutOffered, isPortfolio: true };
  };

  // Calendar helpers
  // Format date as YYYY-MM-DD in local timezone (not UTC)
  const formatDateLocal = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    let startDay = firstDay.getDay() - 1;
    if (startDay === -1) startDay = 6;
    
    const days = [];
    
    const prevMonth = new Date(year, month, 0);
    const prevMonthDays = prevMonth.getDate();
    for (let i = startDay - 1; i >= 0; i--) {
      const day = prevMonthDays - i;
      const d = new Date(year, month - 1, day);
      days.push({
        date: formatDateLocal(d),
        day,
        isCurrentMonth: false,
      });
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const d = new Date(year, month, day);
      days.push({
        date: formatDateLocal(d),
        day,
        isCurrentMonth: true,
      });
    }
    
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const d = new Date(year, month + 1, day);
      days.push({
        date: formatDateLocal(d),
        day,
        isCurrentMonth: false,
      });
    }
    
    return days;
  };

  const getNextMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 1);
  };

  // Get the relevant date for an opportunity
  // Logic: If state is before "Presentada" (i.e., Detectada, Propuesta), show expected_delivery_date
  // If state is "Presentada" or later and has delivery_date, show delivery_date
  const getOpportunityDate = (opp) => {
    const statesBeforePresentada = ['Detectada', 'Propuesta'];
    const isBeforePresentada = statesBeforePresentada.includes(opp.stage || opp.status);
    
    // If the opportunity went back to an earlier state, show expected_delivery_date (next delivery)
    if (isBeforePresentada) {
      if (opp.expected_delivery_date || opp.expected_close_date) {
        return { date: opp.expected_delivery_date || opp.expected_close_date, isDelivered: false };
      }
      return null;
    }
    
    // For "Presentada" or later states, show delivery_date if exists
    if (opp.delivery_date) {
      return { date: opp.delivery_date, isDelivered: true };
    }
    // Otherwise show expected delivery date
    if (opp.expected_delivery_date || opp.expected_close_date) {
      return { date: opp.expected_delivery_date || opp.expected_close_date, isDelivered: false };
    }
    return null;
  };

  const getOpportunitiesForDate = (date) => {
    const filteredOpps = getFilteredOpportunities();
    return filteredOpps.filter(opp => {
      const oppDate = getOpportunityDate(opp);
      return oppDate && oppDate.date === date;
    });
  };

  const getOpportunityCountForDate = (date) => {
    const opps = getOpportunitiesForDate(date);
    const statesBeforePresentada = ['Detectada', 'Propuesta'];
    
    const delivered = opps.filter(o => {
      const isBeforePresentada = statesBeforePresentada.includes(o.stage || o.status);
      return !isBeforePresentada && o.delivery_date === date;
    }).length;
    
    const pending = opps.filter(o => {
      const oppDate = getOpportunityDate(o);
      return oppDate && !oppDate.isDelivered && oppDate.date === date;
    }).length;
    
    return { delivered, pending };
  };

  const goToPrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
    const today = formatDateLocal(new Date());
    setSelectedDate(today);
  };

  const today = formatDateLocal(new Date());
  const selectedDateOpportunities = selectedDate ? getOpportunitiesForDate(selectedDate) : [];

  // Calendar Month Component
  const CalendarMonth = ({ date }) => {
    const days = getDaysInMonth(date);
    const monthName = MONTHS_ES[date.getMonth()];
    const year = date.getFullYear();

    return (
      <div className="select-none">
        <div className="text-center mb-4">
          <h3 className="font-semibold text-lg">{monthName} {year}</h3>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {DAYS_ES.map(day => (
            <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((dayInfo, idx) => {
            const counts = getOpportunityCountForDate(dayInfo.date);
            const isToday = dayInfo.date === today;
            const isSelected = dayInfo.date === selectedDate;
            const hasDelivered = counts.delivered > 0;
            const hasPending = counts.pending > 0;

            return (
              <button
                key={idx}
                onClick={() => setSelectedDate(dayInfo.date)}
                className={`
                  relative aspect-square p-1 rounded-lg text-sm transition-all
                  ${!dayInfo.isCurrentMonth ? 'text-muted-foreground/40' : 'text-foreground'}
                  ${isToday ? 'ring-2 ring-primary ring-offset-2' : ''}
                  ${isSelected ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}
                `}
              >
                <span className="relative z-10">{dayInfo.day}</span>
                {(hasDelivered || hasPending) && (
                  <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                    {hasPending && (
                      <div className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-primary-foreground' : 'bg-orange-500'}`} />
                    )}
                    {hasDelivered && (
                      <div className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-primary-foreground' : 'bg-green-500'}`} />
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Opportunity Card Component (used in Calendar view)
  const OpportunityCard = ({ opp, compact = false }) => {
    const title = opp.title || opp.name || 'Sin título';
    const stage = opp.stage || 'Detectada';
    const oppDate = getOpportunityDate(opp);
    
    // Determine company type for coloring
    const companyType = opp.typsa_org_unit_id ? 'typsa' : getCompanyType(opp.company_id);
    const companyTypeColors = {
      typsa: 'bg-red-50 border-red-200',
      privada: 'bg-purple-50 border-purple-200',
      publica: 'bg-green-50 border-green-200',
    };
    const cardBg = companyTypeColors[companyType] || '';
    
    return (
      <div 
        className={`group relative border rounded-lg transition-all hover:shadow-md cursor-pointer border-l-4 ${stageColors[stage] || 'border-l-gray-300'} ${cardBg || 'bg-card'} ${compact ? 'p-3' : 'p-4'}`}
        onClick={() => handleView(opp)}
        data-testid={`opportunity-card-${opp.id}`}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleView(opp); }}>
              <Eye className="w-4 h-4 mr-2" />
              Ver Detalle
            </DropdownMenuItem>
            {isEditor && (
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEdit(opp); }}>
                <Pencil className="w-4 h-4 mr-2" />
                Editar
              </DropdownMenuItem>
            )}
            {isEditor && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={(e) => { e.stopPropagation(); setDeleteConfirm(opp); }}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash className="w-4 h-4 mr-2" />
                  Eliminar
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className={`font-medium ${compact ? 'text-sm' : ''} pr-8 mb-2`}>{title}</div>
        
        <div className={`flex flex-wrap items-center gap-2 ${compact ? 'text-xs' : 'text-sm'}`}>
          <Badge className={`${stageBadgeColors[stage] || 'bg-gray-100'} text-xs`}>
            {stage}
          </Badge>
          
          {/* Company type indicator */}
          {companyType && (
            <Badge className={`text-xs ${
              companyType === 'typsa' ? 'bg-red-100 text-red-700' :
              companyType === 'privada' ? 'bg-purple-100 text-purple-700' :
              companyType === 'publica' ? 'bg-green-100 text-green-700' : ''
            }`}>
              {companyType === 'typsa' ? 'TYPSA' : companyType === 'privada' ? 'Privada' : 'Pública'}
            </Badge>
          )}
          
          {/* Feb 2026 rules: offered_amount (green + "ofertado"), presupuesto
              base solo en Pública sin oferta (gris), Privada/TYPSA sin oferta
              muestra un dash. */}
          {(() => {
            const scopeC = opp.typsa_org_unit_id ? 'typsa' : (getCompanyType(opp.company_id) === 'publica' ? 'publica' : 'privada');
            const hasOff = opp.offered_amount !== null && opp.offered_amount !== undefined
              && opp.offered_amount !== '' && Number(opp.offered_amount) > 0;
            if (hasOff) {
              return (
                <span className="font-semibold text-green-600">
                  {formatEuro(opp.offered_amount)}
                </span>
              );
            }
            if (scopeC === 'publica' && opp.estimated_value) {
              return (
                <span className="text-xs text-muted-foreground italic">
                  {formatEuro(opp.estimated_value)} (presupuesto base)
                </span>
              );
            }
            return null;
          })()}
        </div>

        {opp.company_id && getCompanyName(opp.company_id) && (
          <div className={`flex items-center gap-1 text-muted-foreground mt-2 ${compact ? 'text-xs' : 'text-sm'}`}>
            <Building2 className="w-3 h-3" />
            <span>{getCompanyName(opp.company_id)}</span>
          </div>
        )}

        {oppDate && (
          <div className={`flex items-center gap-1 mt-2 ${compact ? 'text-xs' : 'text-sm'} ${oppDate.isDelivered ? 'text-green-600' : 'text-muted-foreground'}`}>
            <CalendarIcon className="w-3 h-3" />
            <span>{oppDate.date}</span>
            {oppDate.isDelivered && <Badge variant="outline" className="text-xs ml-1">Entregada</Badge>}
          </div>
        )}

        {(opp.relevance || opp.probability) && (
          <div className="flex flex-wrap gap-1 mt-2">
            {opp.relevance && (
              <Badge className={`text-xs ${relevanceBadgeColors[opp.relevance] || 'bg-gray-100'}`}>
                {opp.relevance}
              </Badge>
            )}
            {opp.probability && (
              <Badge variant="outline" className="text-xs">
                <TrendingUp className="w-3 h-3 mr-1" />
                {opp.probability}
              </Badge>
            )}
          </div>
        )}
      </div>
    );
  };

  // Reusable function to render an opportunity card
  const renderOpportunityCard = (opp, stage) => {
    const scope = getOpportunityScope(opp, getCompanyLookup());
    const cardTitle = displayOppTitle(opp);
    const fullTitle = opp.title || opp.name || '';
    const deliveryDate = opp.expected_delivery_date || opp.expected_close_date;

    // Feb 2026 money-label rules:
    // • Show offered_amount in green with "(ofertado)" suffix and TDS chip.
    // • On Pública WITHOUT offered_amount, show estimated_value in grey with
    //   "(presupuesto base)" — smaller, no green colour, never contributes.
    // • On Privada/TYPSA WITHOUT offered_amount, show "Sin importe ofertado".
    const hasOffered = opp.offered_amount !== null && opp.offered_amount !== undefined
      && opp.offered_amount !== '' && Number(opp.offered_amount) > 0;
    const publicBudget = scope === 'publica' && !hasOffered && opp.estimated_value;

    return (
      <Card 
        key={opp.id} 
        data-testid={`pipeline-opportunity-${opp.id}`} 
        className={`relative cursor-pointer hover:shadow-md transition-shadow border-l-4 ${stageColors[stage] || 'border-l-gray-300'}`}
        onClick={() => handleView(opp)}
      >
        <CardContent className="p-3">
          {/* Three-dot menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-1 right-1 h-6 w-6"
                data-testid={`opportunity-menu-${opp.id}`}
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem 
                onClick={(e) => { e.stopPropagation(); handleView(opp); }}
                data-testid={`view-opportunity-${opp.id}`}
              >
                <Eye className="w-4 h-4 mr-2" />
                Ver Detalle
              </DropdownMenuItem>
              {isEditor && (
                <DropdownMenuItem 
                  onClick={(e) => { e.stopPropagation(); handleEdit(opp); }}
                  data-testid={`edit-opportunity-${opp.id}`}
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Editar
                </DropdownMenuItem>
              )}
              {isEditor && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={(e) => { e.stopPropagation(); setDeleteConfirm(opp); }}
                    className="text-destructive focus:text-destructive"
                    data-testid={`delete-opportunity-${opp.id}`}
                  >
                    <Trash className="w-4 h-4 mr-2" />
                    Eliminar
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="flex items-center gap-1.5 mb-1 pr-6">
            {(() => {
              // Scope badge — misma paleta que el filtro superior.
              const SCOPE_MAP = {
                typsa_gg:    { label: 'TYPSA',    cls: 'bg-red-100 text-red-700 border-red-200' },
                typsa_colab: { label: 'Colab.',   cls: 'bg-sky-100 text-sky-700 border-sky-200' },
                privada:     { label: 'Privada',  cls: 'bg-purple-100 text-purple-700 border-purple-200' },
                publica:     { label: 'Pública',  cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
              };
              const info = SCOPE_MAP[scope];
              if (!info) return null;
              return (
                <Badge
                  variant="outline"
                  className={`text-[10px] px-1.5 py-0 h-4 font-medium ${info.cls}`}
                  data-testid={`opportunity-scope-badge-${opp.id}`}
                  title={scope === 'typsa_colab' ? 'Colaboración TYPSA (proyecto en marcha)' : info.label}
                >
                  {info.label}
                </Badge>
              );
            })()}
          </div>
          <div className="font-medium text-sm mb-1 pr-6 line-clamp-2" title={fullTitle}>{cardTitle}</div>
          {/* Iter 95: en Ganada mostrar importe final = award + ampliaciones */}
          {opp.stage === 'Ganada' ? (() => {
            const finalAward = getFinalAwardAmount(opp);
            const finalTds   = getFinalTdsAmount(opp);
            const extSum     = getExtensionsSum(opp);
            if (finalAward === null || finalAward <= 0) return null;
            return (
              <div className="text-sm font-semibold text-green-700 mb-1" data-testid={`card-amount-${opp.id}`}>
                {formatEuro(finalAward)}
                <span className="text-xs font-normal text-muted-foreground ml-1">
                  {extSum > 0 ? '(adjudicado + ampliaciones)' : '(adjudicado)'}
                </span>
                {finalTds !== null && finalTds > 0 && finalTds !== finalAward && (
                  <span className="text-xs font-normal text-blue-600 ml-2">TDS {formatEuro(finalTds)}</span>
                )}
                {extSum > 0 && (
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    Base {formatEuro(opp.award_amount || 0)} + Ampl. {formatEuro(extSum)}
                  </div>
                )}
              </div>
            );
          })() : hasOffered ? (
            <div className="text-sm font-semibold text-green-600 mb-1" data-testid={`card-amount-${opp.id}`}>
              {formatEuro(opp.offered_amount)}
              <span className="text-xs font-normal text-muted-foreground ml-1">(ofertado)</span>
              {opp.tds_amount ? (
                <span className="text-xs font-normal text-blue-600 ml-2">TDS {formatEuro(opp.tds_amount)}</span>
              ) : null}
            </div>
          ) : publicBudget ? (
            <div className="text-xs text-muted-foreground mb-1 italic" data-testid={`card-amount-${opp.id}`}>
              {formatEuro(opp.estimated_value)} (presupuesto base)
            </div>
          ) : (
            <div className="text-xs text-muted-foreground mb-1 italic" data-testid={`card-amount-${opp.id}`}>
              Sin importe ofertado
            </div>
          )}
          {deliveryDate && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
              <CalendarIcon className="w-3 h-3" />
              <span>{deliveryDate}</span>
            </div>
          )}
          {/* Relevance and Probability badges */}
          {(opp.relevance || opp.probability) && (
            <div className="flex flex-wrap gap-1 mt-1">
              {opp.relevance && (
                <Badge className={`text-xs ${relevanceBadgeColors[opp.relevance] || 'bg-gray-100'}`}>
                  {opp.relevance}
                </Badge>
              )}
              {opp.probability && (
                <Badge variant="outline" className="text-xs">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  {opp.probability}
                </Badge>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-4 md:p-8 w-full max-w-full overflow-x-hidden" data-testid="pipeline-page">
      <div className="flex flex-col gap-4 mb-6 w-full max-w-full">
        <div className="w-full max-w-full overflow-hidden">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight truncate" data-testid="pipeline-title">Pipeline de Oportunidades</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {getFilteredOpportunities().length} oportunidades
            {(clientTypeFilter.length < 4 || ownerFilter !== 'all' || periodFilter !== 'all') && (
              <span className="text-xs ml-1">(filtrado)</span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full max-w-full">
          {/* View mode buttons */}
          <div className="flex rounded-lg border p-1 bg-muted/30 flex-shrink-0">
            <Button
              variant={viewMode === 'kanban' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('kanban')}
              className="h-8 px-2 sm:px-3 shrink-0"
              data-testid="kanban-tab-btn"
            >
              <LayoutGrid className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Kanban</span>
            </Button>
            <Button
              variant={viewMode === 'calendar' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('calendar')}
              className="h-8 px-2 sm:px-3 shrink-0"
              data-testid="calendario-tab-btn"
            >
              <CalendarIcon className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Calendario</span>
            </Button>
            <Button
              variant={viewMode === 'valoradas' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('valoradas')}
              className="h-8 px-2 sm:px-3 shrink-0"
              data-testid="valoradas-tab-btn"
            >
              <span className="hidden sm:inline">Valoradas</span>
              <span className="sm:hidden">Val</span>
            </Button>
            <Button
              variant={viewMode === 'analytics' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('analytics')}
              className="h-8 px-2 sm:px-3 shrink-0"
              data-testid="analytics-tab"
            >
              <BarChart3 className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Analíticas</span>
            </Button>
          </div>
          
          {/* Filtro por tipo de cliente */}
          <div className="flex items-center gap-1 p-1 rounded-lg border bg-muted/30 flex-shrink-0" data-testid="client-type-filter">
            <button
              onClick={() => toggleClientTypeFilter('typsa_gg')}
              className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all ${
                clientTypeFilter.includes('typsa_gg')
                  ? 'bg-red-600 text-white shadow-sm'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
              data-testid="filter-typsa"
              title="TYPSA (GG / I+D+i)"
            >
              <div className={`w-2 h-2 rounded-full ${clientTypeFilter.includes('typsa_gg') ? 'bg-white' : 'bg-red-600'}`} />
              TYPSA
            </button>
            <button
              onClick={() => toggleClientTypeFilter('typsa_colab')}
              className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all ${
                clientTypeFilter.includes('typsa_colab')
                  ? 'bg-sky-600 text-white shadow-sm'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
              data-testid="filter-typsa-colab"
              title="Colaboración TYPSA (proyectos en marcha)"
            >
              <div className={`w-2 h-2 rounded-full ${clientTypeFilter.includes('typsa_colab') ? 'bg-white' : 'bg-sky-600'}`} />
              Colab.
            </button>
            <button
              onClick={() => toggleClientTypeFilter('privada')}
              className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all ${
                clientTypeFilter.includes('privada')
                  ? 'bg-purple-500 text-white shadow-sm'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
              data-testid="filter-privada"
            >
              <div className={`w-2 h-2 rounded-full ${clientTypeFilter.includes('privada') ? 'bg-white' : 'bg-purple-500'}`} />
              Privada
            </button>
            <button
              onClick={() => toggleClientTypeFilter('publica')}
              className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all ${
                clientTypeFilter.includes('publica')
                  ? 'bg-green-500 text-white shadow-sm'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
              data-testid="filter-publica"
            >
              <div className={`w-2 h-2 rounded-full ${clientTypeFilter.includes('publica') ? 'bg-white' : 'bg-green-500'}`} />
              Pública
            </button>
          </div>

          {viewMode === 'kanban' && <GroupBySelector value={groupBy} onChange={setGroupBy} />}
          
          {/* Filtro por periodo */}
          <div className="flex items-center gap-1 flex-shrink-0" data-testid="period-filter">
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="h-9 w-7 flex items-center justify-center text-muted-foreground hover:text-foreground"
                    aria-label="Qué significa cada filtro de periodo"
                    data-testid="period-filter-info"
                  >
                    <Info className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="start" className="max-w-xs text-xs leading-relaxed">
                  <div className="font-semibold mb-1">¿Qué incluye cada filtro?</div>
                  <div className="space-y-1">
                    <div><strong>Semana en curso:</strong> del lunes al domingo de esta semana.</div>
                    <div><strong>Mes en curso:</strong> del día 1 al último día del mes actual.</div>
                    <div><strong>Trimestre en curso:</strong> del primer al último día del trimestre natural (Q1 ene-mar · Q2 abr-jun · Q3 jul-sep · Q4 oct-dic).</div>
                    <div><strong>Año en curso:</strong> del 1 de enero al 31 de diciembre del año actual.</div>
                    <div className="pt-1 text-[11px] text-muted-foreground">
                      Las oportunidades se filtran por la <em>fecha del último cambio de estado</em> (la fecha en que entró en su estado actual).
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant={periodFilter !== 'all' ? 'default' : 'outline'} 
                  size="sm" 
                  className={`h-9 gap-1 ${periodFilter !== 'all' ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
                  data-testid="period-filter-trigger"
                >
                  <CalendarIcon className="w-4 h-4" />
                  <span className="hidden sm:inline">{getPeriodLabel(periodFilter)}</span>
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuItem onClick={() => setPeriodFilter('all')} className="gap-2">
                  {periodFilter === 'all' && <Check className="w-4 h-4" />}
                  <span className={periodFilter === 'all' ? 'font-medium' : ''}>Todo</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setPeriodFilter('week')} className="gap-2">
                  {periodFilter === 'week' && <Check className="w-4 h-4" />}
                  <span className={periodFilter === 'week' ? 'font-medium' : ''}>Semana en curso</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setPeriodFilter('month')} className="gap-2">
                  {periodFilter === 'month' && <Check className="w-4 h-4" />}
                  <span className={periodFilter === 'month' ? 'font-medium' : ''}>Mes en curso</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setPeriodFilter('quarter')} className="gap-2">
                  {periodFilter === 'quarter' && <Check className="w-4 h-4" />}
                  <span className={periodFilter === 'quarter' ? 'font-medium' : ''}>Trimestre en curso</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setPeriodFilter('year')} className="gap-2">
                  {periodFilter === 'year' && <Check className="w-4 h-4" />}
                  <span className={periodFilter === 'year' ? 'font-medium' : ''}>Año en curso</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => { setPeriodFilter('custom'); setDatePickerOpen(true); }} 
                  className="gap-2"
                >
                  {periodFilter === 'custom' && <Check className="w-4 h-4" />}
                  <span className={periodFilter === 'custom' ? 'font-medium' : ''}>Fechas personalizadas...</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Clear filter button */}
            {periodFilter !== 'all' && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-9 w-9 p-0"
                onClick={clearPeriodFilter}
                title="Limpiar filtro de periodo"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
          
          {/* Filtro por responsable */}
          <div className="flex items-center gap-1 flex-shrink-0" data-testid="owner-filter">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant={ownerFilter !== 'all' ? 'default' : 'outline'} 
                  size="sm" 
                  className={`h-9 gap-1 ${ownerFilter !== 'all' ? 'bg-indigo-500 hover:bg-indigo-600' : ''}`}
                  data-testid="owner-filter-trigger"
                >
                  <Filter className="w-4 h-4" />
                  <span className="hidden sm:inline truncate max-w-[120px]">
                    {ownerFilter === 'all' ? 'Todos' : ownerFilter === 'me' ? 'Yo' : ownerFilter === 'unassigned' ? 'Sin asignar' : ownerFilter.split(' ')[0]}
                  </span>
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 max-h-80 overflow-y-auto">
                <DropdownMenuItem 
                  onClick={() => setOwnerFilter('me')} 
                  className="gap-2"
                >
                  {ownerFilter === 'me' && <Check className="w-4 h-4" />}
                  <span className={ownerFilter === 'me' ? 'font-medium' : ''}>Yo ({currentUser?.name?.split(' ')[0] || 'Mi usuario'})</span>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => setOwnerFilter('all')} 
                  className="gap-2"
                >
                  {ownerFilter === 'all' && <Check className="w-4 h-4" />}
                  <span className={ownerFilter === 'all' ? 'font-medium' : ''}>Todos los responsables</span>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => setOwnerFilter('unassigned')} 
                  className="gap-2"
                >
                  {ownerFilter === 'unassigned' && <Check className="w-4 h-4" />}
                  <span className={ownerFilter === 'unassigned' ? 'font-medium' : ''}>Sin asignar</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {getUniqueOwners().map((owner) => (
                  <DropdownMenuItem 
                    key={owner} 
                    onClick={() => setOwnerFilter(owner)} 
                    className="gap-2"
                  >
                    {ownerFilter === owner && <Check className="w-4 h-4" />}
                    <span className={ownerFilter === owner ? 'font-medium' : ''}>{owner}</span>
                  </DropdownMenuItem>
                ))}
                {getUniqueOwners().length === 0 && (
                  <DropdownMenuItem disabled className="text-muted-foreground text-sm">
                    No hay responsables asignados
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Clear owner filter button */}
            {ownerFilter !== 'all' && ownerFilter !== 'me' && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-9 w-9 p-0"
                onClick={() => setOwnerFilter('me')}
                title="Volver a mis oportunidades"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
          
          {/* Date range picker dialog */}
          {datePickerOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDatePickerOpen(false)}>
              <div className="bg-background rounded-lg shadow-lg" onClick={e => e.stopPropagation()}>
                <div className="p-4 border-b">
                  <p className="text-sm font-medium">Selecciona un rango de fechas</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {customDateRange.from && customDateRange.to 
                      ? `${customDateRange.from.toLocaleDateString('es-ES')} - ${customDateRange.to.toLocaleDateString('es-ES')}`
                      : 'Haz clic en dos fechas para definir el rango'}
                  </p>
                </div>
                <div className="p-2">
                  <Calendar
                    mode="range"
                    selected={customDateRange}
                    onSelect={(range) => {
                      setCustomDateRange(range || { from: null, to: null });
                    }}
                    numberOfMonths={2}
                    className="rounded-md border-0"
                  />
                </div>
                <div className="p-4 border-t flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setDatePickerOpen(false); setPeriodFilter('all'); }}>
                    Cancelar
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={() => setDatePickerOpen(false)}
                    disabled={!customDateRange.from || !customDateRange.to}
                  >
                    Aplicar
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
            <ExportMenu endpoint="opportunities" />
            {isEditor && (
              <Button onClick={() => { setEditingOpportunity(null); setDialogOpen(true); }} data-testid="add-opportunity-pipeline-btn" className="shrink-0">
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Nueva Oportunidad</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Cargando...</div>
      ) : opportunities.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-muted-foreground mb-4">No hay oportunidades</div>
          {isEditor && (
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Crear primera oportunidad
            </Button>
          )}
        </div>
      ) : viewMode === 'kanban' ? (
        <>
          {/* Mobile Kanban View - Tabs + Swipeable */}
          <div className="md:hidden flex flex-col min-h-0 w-full max-w-full overflow-x-hidden">
            {/* Sticky Header - Stage Tabs */}
            <div className="bg-background pb-2 flex-shrink-0 w-full">
              {/* Swipe hint */}
              <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground mb-2">
                <ChevronLeft className="w-3 h-3" />
                <span>Desliza para cambiar de estado</span>
                <ChevronRight className="w-3 h-3" />
              </div>
              
              {/* Stage Tabs - Fixed width container */}
              <div className="flex overflow-x-auto gap-1 pb-2 scrollbar-hide w-full">
                {stages.map((stage) => {
                  const count = getOpportunitiesByStage(stage).length;
                  const isActive = mobileActiveStage === stage;
                  return (
                    <button
                      key={stage}
                      onClick={() => setMobileActiveStage(stage)}
                      className={`flex-shrink-0 px-3 py-2 rounded-lg text-sm font-medium transition-all border-l-4 ${stageColors[stage]} ${
                        isActive 
                          ? 'bg-card shadow-md border border-border' 
                          : 'bg-muted/30 hover:bg-muted/50'
                      }`}
                    >
                      <span className="whitespace-nowrap">{stage}</span>
                      <Badge variant={isActive ? 'default' : 'secondary'} className="ml-2 text-xs">
                        {count}
                      </Badge>
                    </button>
                  );
                })}
              </div>
              
              {/* Swipe indicator dots */}
              <div className="flex justify-center gap-1.5 pt-1">
                {stages.map((stage) => (
                  <button
                    key={stage}
                    onClick={() => setMobileActiveStage(stage)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      mobileActiveStage === stage 
                        ? 'bg-primary w-4' 
                        : 'bg-muted-foreground/30'
                    }`}
                  />
                ))}
              </div>
            </div>
            
            {/* Scrollable Content */}
            <div 
              className="pb-4"
              onTouchStart={(e) => {
                const touch = e.touches[0];
                e.currentTarget.dataset.startX = touch.clientX;
                e.currentTarget.dataset.startY = touch.clientY;
              }}
              onTouchEnd={(e) => {
                const startX = parseFloat(e.currentTarget.dataset.startX || '0');
                const startY = parseFloat(e.currentTarget.dataset.startY || '0');
                const endX = e.changedTouches[0].clientX;
                const endY = e.changedTouches[0].clientY;
                const diffX = startX - endX;
                const diffY = startY - endY;
                const threshold = 50;
                
                // Only trigger swipe if horizontal movement is greater than vertical
                if (Math.abs(diffX) > threshold && Math.abs(diffX) > Math.abs(diffY) * 1.5) {
                  const currentIndex = stages.indexOf(mobileActiveStage);
                  if (diffX > 0 && currentIndex < stages.length - 1) {
                    // Swipe left - next stage
                    setMobileActiveStage(stages[currentIndex + 1]);
                  } else if (diffX < 0 && currentIndex > 0) {
                    // Swipe right - previous stage
                    setMobileActiveStage(stages[currentIndex - 1]);
                  }
                }
              }}
            >
              {/* Stage Header Card */}
              {(() => {
                const stageOpps = getOpportunitiesByStage(mobileActiveStage);
                const sub = getStageSubtotal(mobileActiveStage);
                const groupedOpps = getGroupedOpportunitiesByStage(mobileActiveStage);
                let subLine = '';
                if (stageOpps.length === 0) subLine = 'Sin oportunidades';
                else if (!sub.isPortfolio || sub.withOffered === 0) subLine = `${stageOpps.length} oportunidades · sin importe ofertado`;
                else {
                  const parts = [`${formatEuro(sub.offeredSum)}`];
                  if (sub.tdsSum > 0) parts.push(`TDS ${formatEuro(sub.tdsSum)}`);
                  if (sub.withoutOffered > 0) parts.push(`${sub.withoutOffered} sin oferta`);
                  subLine = parts.join(' · ');
                }
                return (
                  <>
                    <Card className={`mb-4 border-l-4 ${stageColors[mobileActiveStage]}`}>
                      <CardHeader className="py-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg font-semibold">{mobileActiveStage}</CardTitle>
                          <div className="flex items-center gap-2">
                            <div className="text-xs font-medium text-right" data-testid={`stage-subtotal-mobile-${mobileActiveStage.toLowerCase()}`}>{subLine}</div>
                            <StageCollapseButton stage={mobileActiveStage} collapsed={!!collapsedStages[mobileActiveStage]} onToggle={toggleStage} testIdPrefix="stage-collapse-mobile" />
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {collapsedStages[mobileActiveStage] ? 'Estado recogido' : `${stageOpps.length} oportunidades`}
                        </p>
                      </CardHeader>
                    </Card>
                    
                    {/* Groups */}
                    <div className="space-y-3">
                      {!collapsedStages[mobileActiveStage] && (
                        <KanbanGroups
                          groups={groupedOpps} keyPrefix={`mobile-${mobileActiveStage}`} groupBy={groupBy} mobile
                          expandedGroups={expandedGroups} onToggle={toggleGroup}
                          renderCard={(opp) => renderOpportunityCard(opp, mobileActiveStage)}
                        />
                      )}
                      
                      {groupedOpps.length === 0 && (
                        <div className="text-center py-8 text-muted-foreground">
                          No hay oportunidades en este estado
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
          
          {/* Desktop Kanban View - Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {stages.map((stage) => {
            const stageOpps = getOpportunitiesByStage(stage);
            const groupedOpps = getGroupedOpportunitiesByStage(stage);
            const sub = getStageSubtotal(stage);
            let subLine = '';
            if (stageOpps.length === 0) subLine = 'Sin oportunidades';
            else if (!sub.isPortfolio || sub.withOffered === 0) subLine = `${stageOpps.length} oportunidades · sin importe ofertado`;
            else {
              const parts = [`${formatEuro(sub.offeredSum)}`];
              if (sub.tdsSum > 0) parts.push(`TDS ${formatEuro(sub.tdsSum)}`);
              if (sub.withoutOffered > 0) parts.push(`${sub.withoutOffered} sin oferta`);
              subLine = parts.join(' · ');
            }

            return (
              <div key={stage} data-testid={`pipeline-stage-${stage.toLowerCase()}`}>
                <Card className={`mb-3 border-l-4 ${stageColors[stage] || 'border-l-gray-300'}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-2">
                      <CardTitle className="text-sm font-semibold uppercase tracking-wider">
                        {stage}
                      </CardTitle>
                      <StageCollapseButton stage={stage} collapsed={!!collapsedStages[stage]} onToggle={toggleStage} />
                    </div>
                    <div className="text-xs text-muted-foreground mt-1" data-testid={`stage-subtotal-${stage.toLowerCase()}`}>
                      {stageOpps.length} • {subLine}
                    </div>
                    {collapsedStages[stage] && stageOpps.length > 0 && (
                      <div className="text-[11px] text-muted-foreground italic mt-1" data-testid={`stage-collapsed-hint-${stage.toLowerCase()}`}>Recogido · pulsa la flecha para ver</div>
                    )}
                  </CardHeader>
                </Card>
                {!collapsedStages[stage] && (
                  <div className="space-y-2">
                    <KanbanGroups
                      groups={groupedOpps} keyPrefix={stage} groupBy={groupBy}
                      expandedGroups={expandedGroups} onToggle={toggleGroup}
                      renderCard={(opp) => renderOpportunityCard(opp, stage)}
                    />
                  </div>
                )}
              </div>
            );
          })}
          </div>
        </>
      ) : viewMode === 'analytics' ? (
        /* Analytics View */
        <OpportunityAnalytics
          clientTypeFilter={clientTypeFilter}
          ownerFilter={ownerFilter}
          periodFilter={periodFilter}
        />
      ) : viewMode === 'valoradas' ? (
        /* Valoradas View — respects Pipeline header filters */
        <OpportunityValoradas
          clientTypeFilter={clientTypeFilter}
          ownerFilter={ownerFilter}
          periodFilter={periodFilter}
          customDateRange={customDateRange}
        />
      ) : (
        /* Calendar View */
        <div className="space-y-6">
          {/* Calendar Navigation */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={goToPrevMonth}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={goToNextMonth}>
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToToday}>
                Hoy
              </Button>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-orange-500" />
                <span className="text-muted-foreground">Fecha límite</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-muted-foreground">Entregadas</span>
              </div>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendars */}
            <div className="lg:col-span-2">
              <Card>
                <CardContent className="p-6">
                  {/* Mobile: Single month */}
                  <div className="lg:hidden">
                    <CalendarMonth date={currentMonth} />
                  </div>
                  
                  {/* Desktop: Two months */}
                  <div className="hidden lg:grid lg:grid-cols-2 lg:gap-8">
                    <CalendarMonth date={currentMonth} />
                    <CalendarMonth date={getNextMonth(currentMonth)} />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Selected Date Opportunities */}
            <div className="lg:col-span-1">
              <Card className="h-full">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">
                    {selectedDate 
                      ? new Date(selectedDate + 'T12:00:00').toLocaleDateString('es-ES', { 
                          weekday: 'long', 
                          day: 'numeric', 
                          month: 'long' 
                        })
                      : 'Selecciona una fecha'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!selectedDate ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <CalendarIcon className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">Haz clic en una fecha para ver las oportunidades</p>
                    </div>
                  ) : selectedDateOpportunities.length === 0 ? (
                    <div className="text-center py-8">
                      <CalendarIcon className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
                      <p className="text-sm text-muted-foreground mb-4">No hay oportunidades para esta fecha</p>
                      {isEditor && (
                        <Button variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
                          <Plus className="w-4 h-4 mr-2" />
                          Crear oportunidad
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {selectedDateOpportunities.map((opp) => (
                        <OpportunityCard key={opp.id} opp={opp} compact />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Upcoming Deadlines */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Próximas fechas límite</CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const filteredOpps = getFilteredOpportunities();
                const upcomingOpps = filteredOpps
                  .filter(o => {
                    const date = o.expected_delivery_date || o.expected_close_date;
                    return date && date >= today && !o.delivery_date;
                  })
                  .sort((a, b) => {
                    const dateA = a.expected_delivery_date || a.expected_close_date;
                    const dateB = b.expected_delivery_date || b.expected_close_date;
                    return dateA.localeCompare(dateB);
                  })
                  .slice(0, 6);
                
                if (upcomingOpps.length === 0) {
                  return (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No hay fechas límite próximas
                    </p>
                  );
                }

                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {upcomingOpps.map((opp) => (
                      <OpportunityCard key={opp.id} opp={opp} compact />
                    ))}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Opportunity Dialog for Create/Edit */}
      <OpportunityDialog 
        open={dialogOpen} 
        onOpenChange={handleDialogClose} 
        onSuccess={fetchData}
        opportunity={editingOpportunity}
      />

      {/* Delete Single Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Oportunidad</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que deseas eliminar "{deleteConfirm?.title || deleteConfirm?.name}"? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting === deleteConfirm?.id}
              data-testid="confirm-delete-btn"
            >
              {deleting === deleteConfirm?.id ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Pipeline;
